import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveFileUrl } from "@shared/blocks/file-ref";
import { useAudioPlayback } from "./audio-player-store";
import "./view.scss";
const WAVEFORM_BAR_WIDTH_PX = 4;
const WAVEFORM_BAR_GAP_PX = 2;
const WAVEFORM_BAR_FALLBACK_COUNT = 32;
function formatDuration(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}
function hashString(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
        h = (h << 5) + h + s.charCodeAt(i);
    }
    return h >>> 0;
}
function smoothstep01(t) {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
}
function placeholderPeak01(seed, barIndex) {
    let x = Math.imul(seed ^ (barIndex * 0x27d4eb2d), 0x9e3779b1);
    x ^= barIndex + (x << 6) + (x >>> 2);
    x ^= x >>> 16;
    x = Math.imul(x, 0x85ebca6b);
    x ^= x >>> 13;
    return (x >>> 0) / 0xffff_ffff;
}
function decorativeWaveformHeights(seed, barCount, taperSpan) {
    const out = [];
    for (let i = 0; i < barCount; i++) {
        const rawT = taperSpan <= 0 ? 1 : Math.min(1, Math.max(0, i / taperSpan));
        const amp = 0.03 + 0.97 * smoothstep01(smoothstep01(rawT));
        const u = placeholderPeak01(seed, i);
        const h = 18 + u * 82;
        out.push(Math.round(h * amp * 10) / 10);
    }
    return out;
}
function isWaveformBarPlayed(progress, index, barCount) {
    return progress > 0 && index / barCount < progress;
}
export const AudioBlockView = memo(({ block, blockParams, transcript: transcriptProp }) => {
    const content = block.content;
    const audioUrl = resolveFileUrl(content.file);
    const isExpanded = blockParams?.isExpanded === true;
    const { isPlaying, progress, duration, toggle, seekTo } = useAudioPlayback(block.id, audioUrl, content.duration ?? 0);
    const fileKey = content.file.type === "external" ? `ext:${content.file.url}` : `int:${content.file.fileId}`;
    const staticFingerprint = useMemo(() => `${String(block.id)}|${fileKey}|${content.duration ?? 0}|${content.format ?? ""}`, [block.id, fileKey, content.duration, content.format]);
    const waveformSeed = useMemo(() => hashString(staticFingerprint), [staticFingerprint]);
    const waveformRef = useRef(null);
    const [barCount, setBarCount] = useState(WAVEFORM_BAR_FALLBACK_COUNT);
    useEffect(() => {
        const el = waveformRef.current;
        if (!el)
            return;
        const recompute = () => {
            const width = el.clientWidth;
            if (width <= 0)
                return;
            const fit = Math.max(1, Math.floor((width + WAVEFORM_BAR_GAP_PX) / (WAVEFORM_BAR_WIDTH_PX + WAVEFORM_BAR_GAP_PX)));
            setBarCount((prev) => (prev === fit ? prev : fit));
        };
        recompute();
        const observer = new ResizeObserver(recompute);
        observer.observe(el);
        return () => observer.disconnect();
    }, [isExpanded]);
    const taperSpan = isExpanded ? 10 : Math.max(3, Math.floor(barCount * 0.58));
    const barHeightsPct = useMemo(() => decorativeWaveformHeights(waveformSeed, barCount, taperSpan), [waveformSeed, barCount, taperSpan]);
    const onTogglePlay = useCallback((e) => {
        e.stopPropagation();
        toggle();
    }, [toggle]);
    const onWaveformClick = useCallback((e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        seekTo(ratio);
    }, [seekTo]);
    const [hoverProgress, setHoverProgress] = useState(null);
    const onWaveformMove = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        setHoverProgress(ratio);
    }, []);
    const onWaveformLeave = useCallback(() => setHoverProgress(null), []);
    const transcriptSource = (transcriptProp ?? block.summary)?.trim() ?? "";
    const headlineText = transcriptSource.length > 0 ? transcriptSource : block.title;
    const durationLabel = duration > 0 ? formatDuration(duration) : "—";
    return (_jsxs("div", { className: `audio-content${isExpanded ? " audio-content--expanded" : ""}`, children: [_jsx("div", { className: "audio-content__main", children: _jsxs("div", { className: "audio-content__meta", children: [_jsx("div", { className: "audio-content__title", children: headlineText }), _jsx("div", { className: "audio-content__length", children: durationLabel })] }) }), _jsxs("div", { className: "audio-content__waveform-container", children: [_jsx("button", { type: "button", className: `audio-content__play-btn ${isPlaying ? "audio-content__play-btn--playing" : ""}`, onClick: onTogglePlay, onMouseDown: (e) => e.stopPropagation(), onPointerDown: (e) => e.stopPropagation(), "aria-label": isPlaying ? "Pause" : "Play", children: isPlaying ? (_jsxs("svg", { width: "10", height: "10", viewBox: "0 0 10 10", "aria-hidden": true, children: [_jsx("rect", { x: "1", y: "1", width: "3", height: "8", fill: "currentColor" }), _jsx("rect", { x: "6", y: "1", width: "3", height: "8", fill: "currentColor" })] })) : (_jsx("svg", { width: "12", height: "13", viewBox: "0 0 12 13", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M11.2324 5.79318C11.6318 6.01406 11.6318 6.56628 11.2324 6.78717L0.898596 12.5026C0.49922 12.7235 -2.01579e-08 12.4474 0 12.0056L5.21585e-07 0.574735C5.41743e-07 0.132962 0.499221 -0.143145 0.898597 0.0777416L11.2324 5.79318Z", fill: "#282828" }) })) }), _jsx("div", { ref: waveformRef, className: "audio-content__waveform", onClick: onWaveformClick, onMouseMove: onWaveformMove, onMouseLeave: onWaveformLeave, onMouseDown: (e) => e.stopPropagation(), onPointerDown: (e) => e.stopPropagation(), children: barHeightsPct.map((pct, i) => {
                            const played = isWaveformBarPlayed(progress, i, barCount);
                            const hovered = hoverProgress !== null && i / barCount < hoverProgress;
                            const cls = played
                                ? "audio-content__waveform-bar audio-content__waveform-bar--played"
                                : hovered
                                    ? "audio-content__waveform-bar audio-content__waveform-bar--hover"
                                    : "audio-content__waveform-bar";
                            return _jsx("span", { className: cls, style: { height: `${pct}%` } }, i);
                        }) })] })] }));
});
